export type MoneyGBP = number;

export type MenuItem = {
  id: string;
  name: string;
  description: string;
  price: MoneyGBP;
  imageUrl?: string;
  calories?: number;
  category: string;
  isSoldOut?: boolean;
  includesDrinkChoice?: boolean;
  eligibleForDiscount?: boolean;
  offersMealUpgrade?: boolean;
  saladSaucePreferenceListId?: string;
  flavourChoiceListId?: string;
};

export type DrinkSelection = {
  menuItemId: string;
  menuItemName: string;
};

export type DealSelection = {
  slotId: string;
  slotLabel: string;
  menuItemId: string;
  menuItemName: string;
};

export type ItemCartLine = {
  kind: 'item';
  lineId: string;
  item: MenuItem;
  quantity: number;
  drinkSelection?: DrinkSelection;
  notes?: string;
  isMeal?: boolean;
  mealUpgradePrice?: number;
  saladSaucePreference?: {
    value: string;
    label: string;
  };
  flavourChoice?: {
    value: string;
    label: string;
  };
};

export const MAX_ITEM_NOTES_LENGTH = 500;

export function normalizeItemNotes(notes?: string): string | undefined {
  const trimmed = notes?.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_ITEM_NOTES_LENGTH);
}

export function itemCartLineId(
  itemId: string,
  drinkMenuItemId?: string,
  notes?: string,
  isMeal?: boolean,
  saladSaucePreferenceValue?: string,
  flavourChoiceValue?: string
): string {
  const drinkPart = drinkMenuItemId ? `:drink:${drinkMenuItemId}` : '';
  const mealPart = isMeal ? ':meal' : '';
  const saladSaucePart = saladSaucePreferenceValue
    ? `:ss:${encodeURIComponent(saladSaucePreferenceValue)}`
    : '';
  const flavourChoicePart = flavourChoiceValue ? `:fl:${encodeURIComponent(flavourChoiceValue)}` : '';
  const normalized = normalizeItemNotes(notes);
  const notesPart = normalized ? `:notes:${encodeURIComponent(normalized)}` : '';
  return `${itemId}${drinkPart}${mealPart}${saladSaucePart}${flavourChoicePart}${notesPart}`;
}

export function itemLineUnitPrice(line: ItemCartLine): number {
  let price = line.item.price;
  if (line.isMeal) price += Number(line.mealUpgradePrice) || 0;
  return price;
}

export type DealCartLine = {
  kind: 'deal';
  lineId: string;
  dealId: string;
  dealName: string;
  dealPrice: MoneyGBP;
  dealImageUrl?: string;
  selections: DealSelection[];
  quantity: number;
  eligibleForDiscount?: boolean;
};

export type CartLine = ItemCartLine | DealCartLine;

export type CartState = {
  lines: CartLine[];
  promoCode?: string;
  promoDiscountAmount?: number;
};

export function cartLinePrice(line: CartLine): number {
  if (line.kind === 'deal') return line.dealPrice;
  return itemLineUnitPrice(line);
}

export function cartLineKey(line: CartLine): string {
  return line.lineId;
}

export function isDealCartLine(line: CartLine): line is DealCartLine {
  return line.kind === 'deal';
}

export function isItemCartLine(line: CartLine): line is ItemCartLine {
  return line.kind === 'item';
}
